import { useEffect, useMemo, useState } from 'react';
import styles from './GenerateSerialNumber.module.scss';
import { connect } from 'react-redux';
import { useOutletContext } from 'react-router-dom';
import { Box, Grid, Typography } from '@mui/material';
import { BasicTable} from '../../components';
import { generateSerialNumber } from '../../redux/actionCreators/serialNumber';
import {
	LOADING_MESSAGE,
	ERROR_MESSAGE,
	DEFAULT_COLUMNS,
	OPTIONAL_COLUMNS,
  OPERATIONS,
	CHASIS_LINE_IDS,
	COLUMNS_TO_HIDE_FOR_CHASSIS_LINE,
} from '../../constants';
import { updateCurrentLineItem } from '../../redux/actionCreators/station';
import { useMessageContextManager } from '../../components/MessageOverlay/MessageOverlay';
import { useRedirectToNextScreen } from '../../hooks';
import JlrButton from '../../components/JlrButton/JlrButton';

const mapStateToProps = (state) => {
	return {
		currentLineItem: state.station.currentLineItem,
		user: state.user,
		themeMode: state.theme.themeMode,
	};
};

const mapDispatchToProps = (dispatch) => ({
	generateSerialNumber: (successCallback, errorCallback, sequenceNumber) =>
		dispatch(
			generateSerialNumber(successCallback, errorCallback, sequenceNumber),
		),
	updateCurrentLineItem: (currentLineItem) =>
		dispatch(updateCurrentLineItem(currentLineItem)),
});

const classNames = {
	paper: {
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
	},
	tableContentContainer: {
		flex: 1,
		backgroundColor: 'background.default',
		borderBottomLeftRadius: '5px',
		borderBottomRightRadius: '5px',
		padding: '20px',
	},
};

export const GenerateSerialNumber = ({
	currentLineItem,
	generateSerialNumber,
	updateCurrentLineItem,
	user,
	themeMode
}) => {
	const [currentAgsnData, setCurrentAgsnData] = useState([]);
	const { showMessageOverlay } = useMessageContextManager();

	const { handleNext } = useOutletContext();
	const redirect = useRedirectToNextScreen();

	const ctaGenerateSerialNumber = () => {
		const closeMesssageOverlay = showMessageOverlay({
			contextText: LOADING_MESSAGE.SERIAL_NUMBER_CONTEXT_TEXT,
			loading: true,
		});

		generateSerialNumber(
			(data) => {
				updateCurrentLineItem({
					...currentAgsnData[0],
					serialNumber: data.serialNumber,
					completedOperations: [
						...new Set([
							...(currentLineItem.completedOperations || []),
							OPERATIONS.SERIAL_NUMBER_GENERATE,
						]),
					],
				});
				closeMesssageOverlay();
				redirect(() => handleNext());
			},
			(error) => {
				closeMesssageOverlay();
				showMessageOverlay({
					contextText:
						ERROR_MESSAGE.ERROR_CONTEXT_TEXT + 'Generate Serial Number.',
					info: error?.response?.data?.message,
					error: true,
				});
				// logError(`Generating serial number ${error}`);
			},
			currentAgsnData[0].sequenceNumber,
		);
	};

	
	const filteredOriginalDefaultColumns = useMemo(() => {
		if (CHASIS_LINE_IDS.includes(user?.lineId)) {
				return DEFAULT_COLUMNS.filter((res) => {
						return !COLUMNS_TO_HIDE_FOR_CHASSIS_LINE.includes(res.field);
				});
		}
		return DEFAULT_COLUMNS; // Return the array directly
}, [DEFAULT_COLUMNS, user?.lineId]);


	useEffect(() => {
		if (Object.keys(currentLineItem).length > 0) {
			setCurrentAgsnData([
				{
					...currentLineItem,
					id: currentLineItem.sequenceNumber,
					readyForDropSequenceNumber: currentLineItem.metaData.readyForDropSequenceNumber,
					vcDescription: currentLineItem.metaData.vcDescription,
					vcSequenceNumber: currentLineItem.metaData.vcSequenceNumber,
					vcPartNumber: currentLineItem.metaData.vcPartNumber,
					shiftLabel: currentLineItem.metaData.shiftLabel,
					vcNumber: currentLineItem.metaData.vcNumber,
				},
			]);
		}
	}, [currentLineItem]);

	return (
		<>
			<BasicTable
				listId='generateSerialNumber'
				rows={currentAgsnData}
				columns={[...filteredOriginalDefaultColumns, ...OPTIONAL_COLUMNS]}
				defaultColumns={[...filteredOriginalDefaultColumns]}
				subtitle="Dropped ASN/AgSN"
				tableStyles={{ paper: classNames.paper }}
			>
				<Box sx={{ 
					...classNames.tableContentContainer,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					gap: 3
				}}>
					{/* Information Section */}
					<Box sx={{ textAlign: 'center', mb: 2 }}>
						<Typography 
							variant="h5" 
							sx={{ 
								mb: 1,
								fontFamily: user.plant === 'Lucknow' ? "'Inter', sans-serif" : 'inherit',
								fontWeight: user.plant === 'Lucknow' ? 600 : 500,
								color: 'text.primary'
							}}
						>
							Ready to Generate Serial Number
						</Typography>
						{currentAgsnData.length > 0 && (
							<Typography 
								variant="body1" 
								color="textSecondary"
								sx={{ 
									fontFamily: user.plant === 'Lucknow' ? "'Inter', sans-serif" : 'inherit',
								}}
							>
								Part: {currentAgsnData[0]?.partNumber} • ASN: {currentAgsnData[0]?.vcSequenceNumber}
							</Typography>
						)}
					</Box>

					{/* Action Button */}
					<Grid container justifyContent="center">
						<Grid item xs={12} sm={8} md={6} lg={4}>
							<JlrButton
								variant="success"
								size="large"
								onClick={ctaGenerateSerialNumber}
								fullWidth
								disabled={currentAgsnData.length === 0}
							>
								Generate Serial Number
							</JlrButton>
						</Grid>
					</Grid>

					{/* Helper Text */}
					<Typography 
						variant="caption" 
						color="textSecondary"
						sx={{ 
							textAlign: 'center',
							fontFamily: user.plant === 'Lucknow' ? "'Inter', sans-serif" : 'inherit',
							mt: 1
						}}
					>
						Click the button above to generate a unique serial number for this part
					</Typography>
				</Box>
			</BasicTable>
		</>
	);
};

export default connect(
	mapStateToProps,
	mapDispatchToProps,
)(GenerateSerialNumber);
